import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Switch,
    FormControlLabel,
    Button,
    Grid,
    ButtonBase,
    Chip,
    LinearProgress
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import { DotLottleLoader } from "../../../components/Loader";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { CustomFieldLabel } from "../../../components/fields/CustomFieldLabel/CustomFieldLabel";
import { CustomFieldText } from "../../../components/fields/CustomFieldText/CustomFieldText";
import { CustomTextArea } from "../../../components/fields/CustomTextArea/CustomTextArea";
import { CustomSelectField } from "../applicationForm/customSelect";
import { statesList } from "../createEditProfile/formFragments/constants";
import { uploadVideo } from "../recording/helper";
import { uploadImage } from "../../profile/helper";
import imagePlaceholder from "../../../components/fields/CustomImageUpload/image-placeholder.jpg";
import VideocamIcon from "@mui/icons-material/Videocam";
import CloseIcon from "@mui/icons-material/Close";
import BrandInlineList from "../../../components/brand/BrandInlineList";
import { useUser } from "../../../hooks/useUser";
import { isProTeam } from "../../../utils";
import { useBoundStore } from "../../../store";
import * as Yup from "yup";
import { updateMyInfo } from "../../../api/endpoints";
import { ProFormTabs } from "../createEditProfile/tabs";
import ContactReminderBanner from "../../../components/ContactReminderBanner/ContactReminderBanner";
import { FilmDateConfirmField } from "../../../components/FilmWindow/FilmDateConfirmField";
import { ExpertCalendar } from "../../../components/FilmingScheduling/ExpertCalendar";
import {
    FilmWindow,
    hasFilmWindow,
    isWithinFilmWindow,
    formatFilmWindow,
    formatFilmDay,
    formatFilmDayList,
    getFilmDays,
    getFilmDaysCount,
    toDay,
    toDayList,
} from "../../../utils/filmWindow";

const DECLINE_REASONS = [
    "Schedule conflict",
    "Already worked with a competing brand",
    "Not a fit for my expertise",
    "Travel doesn't work",
    "Other"
];

const PHONE_REGEX = /^[+]?[\d\s().-]{7,20}$/;

const contactValidationSchema = Yup.object({
    phoneNumber: Yup.string().when("interested", {
        is: true,
        then: (schema: Yup.StringSchema) =>
            schema
                .trim()
                .required("Phone number is required")
                .matches(PHONE_REGEX, "Enter a valid phone number"),
        otherwise: (schema: Yup.StringSchema) => schema
    })
});

// Design tokens (portal theme tokens; see PORTAL-THEME-PLAN.md)
const T = {
    bronze: "var(--accent)",
    // Fill-only deep gold under white text — gold is theme-constant and no token
    // matches this depth, so it stays literal. Gold *text* uses --accent-text.
    bronzeDeep: "#80582A",
    bronzeSoft: "var(--tint)",

    text: "var(--text)",
    textSoft: "var(--text-2)",
    textMute: "var(--muted)",
    rule: "var(--border)",
    ruleStrong: "var(--border)",
    surface: "var(--surface)",
    canvas: "var(--page)",
    declineBg: "var(--down-bg)",
    declineText: "var(--down)",
    acceptBg: "var(--up-bg)",
    acceptText: "var(--up)"
};

const cardSx = {
    backgroundColor: T.surface,
    border: `1px solid ${T.rule}`,
    borderRadius: "12px",
    boxShadow: "var(--sh-1)",
    p: 3
};

const eyebrowSx = {
    fontFamily: "CircularStd",
    fontSize: "10px",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    color: "var(--accent-text)"
};

// ── Unsent-answers draft ──────────────────────────────────────────────────
// Everything the expert enters is mirrored to localStorage until a submit
// succeeds, so a failed attempt (API timeout, stalled upload) or a reload
// never costs them the form — that is how a filming location went missing on
// 2026-09-21 (entered on an attempt that never persisted, not re-entered on
// the one that did). Per user + project, expires after a week. localStorage
// can throw (private mode, blocked storage), so every access is guarded.
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const draftKeyFor = (userId: number | string | undefined, projectId: string | undefined) =>
    `confirmation-draft:${userId ?? "me"}:${projectId ?? ""}`;

type ConfirmationDraft = {
    savedAt: number;
    values: Record<string, any>;
    preferredDays: string[];
    unavailableDateRange: [string | null, string | null];
    dateConfirmed: boolean | null;
    declineReason: string;
    locationImages: string[];
    videoPath: string;
};

const readDraft = (key: string): ConfirmationDraft | null => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.savedAt !== "number") return null;
        if (Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
            localStorage.removeItem(key);
            return null;
        }
        return parsed as ConfirmationDraft;
    } catch {
        return null;
    }
};

const writeDraft = (key: string, draft: Omit<ConfirmationDraft, "savedAt">) => {
    try {
        localStorage.setItem(key, JSON.stringify({ ...draft, savedAt: Date.now() }));
    } catch {
        // Storage unavailable — the form still works, it just won't survive a reload.
    }
};

const clearDraft = (key: string) => {
    try {
        localStorage.removeItem(key);
    } catch {
        // ignore
    }
};

// Lives inside <Formik> so it can read live values. Writes the draft whenever
// anything differs from what the form started with (server values, or a
// restored draft), so an untouched form never leaves a draft behind.
const DraftPersist = ({
    draftKey,
    enabled,
    extra
}: {
    draftKey: string;
    enabled: boolean;
    extra: Omit<ConfirmationDraft, "savedAt" | "values">;
}) => {
    const { values } = useFormikContext<Record<string, any>>();
    const serialized = JSON.stringify({ values, ...extra });
    const baseline = React.useRef<string | null>(null);
    const lastWritten = React.useRef<string | null>(null);
    useEffect(() => {
        if (!enabled) return;
        if (baseline.current === null) {
            baseline.current = serialized;
            return;
        }
        if (serialized === baseline.current || serialized === lastWritten.current) return;
        lastWritten.current = serialized;
        writeDraft(draftKey, { values, ...extra });
    }, [draftKey, enabled, serialized, values, extra]);
    return null;
};

const TIMEOUT_MESSAGE =
    "The server took too long to respond. Your answers are saved on this device — please try again.";

// The API's control middleware sends ApiError codes as a bare JSON string or
// { error }. Map the ones this form can hit to copy the expert can act on;
// anything else still shows its code so support can search for it.
const submitErrorMessage = (error: any, filmWindow: FilmWindow | null): string => {
    if (error?.code === "ECONNABORTED") return TIMEOUT_MESSAGE;
    const data = error?.response?.data;
    const code: string | undefined = typeof data === "string" ? data : data?.error;
    switch (code) {
        case "RequestTimeout":
            return TIMEOUT_MESSAGE;
        case "SelectionLocked":
            return "This project's expert roster is locked, so new opt-ins aren't being accepted right now. Please contact your producer.";
        case "OptInsClosed":
            return "Opt-ins for this project are closed — the shoot is already in pre-production. Please contact your producer if you have questions.";
        case "PreferredDateRequired":
            return "Select one preferred filming date.";
        case "PreferredDateOutsideFilmWindow":
            return hasFilmWindow(filmWindow)
                ? `Pick a date within ${formatFilmWindow(filmWindow)}.`
                : "Your preferred date is outside the filming window.";
        case "FilmDateNotSet":
        case "FilmDaysIncomplete":
            return "The filming dates aren't finalized yet — you can confirm your availability once they are.";
        case "IntroVideoRequired":
            return "Upload an introduction video to continue.";
        case "ProjectNotFound":
            return "This project is no longer available.";
        default:
            break;
    }
    if (!error?.response) {
        return "We couldn't reach the server. Check your connection — your answers are saved on this device.";
    }
    return code ? `Failed to submit (${code}). Please try again.` : "Failed to submit. Please try again.";
};

// R3 (hybrid) note: the confirmation record (opt-in, availability,
// product-received) is PROCESS-shared — the backend
// /expert/projects/:projectId/confirmation resolves the project to its
// selection process, so it's already correct for any sibling projectId. The
// `project.film_date` shown here is intentionally PER-PROJECT (scheduling stays
// per-project under the hybrid decision), so reading it from this projectId is
// correct — do not "fix" it to a shared value. (Making shipping status truly
// one-shipment-per-process is a separate backend follow-up; the columns are
// still per-project today.)
const ConfirmationDetails = () => {
    const { projectId, userId } = useParams<{ projectId: string; userId?: string }>();
    const navigate = useNavigate();
    const isAdminView = !!userId;
    const { user, role } = useUser();
    // Admin, AM and the expert team may answer for an expert; every other
    // internal role that can open this page stays read-only.
    const canAnswerForExpert = isAdminView && isProTeam(role);
    const readOnly = isAdminView && !canAnswerForExpert;
    const updateUserInfo = useBoundStore((state) => state.updateUserInfo);
    const [loading, setLoading] = useState(true);
    const [project, setProject] = useState<any>(null);
    // Every project this ONE response covers (the roster row is per selection
    // process — same product, brief and film date).
    const [processProjects, setProcessProjects] = useState<
        Array<{ id: number; title?: string; content_type?: { name?: string } | null }>
    >([]);
    const [preferredDays, setPreferredDays] = useState<string[]>([]);
    const [filmWindow, setFilmWindow] = useState<FilmWindow | null>(null);
    const togglePreferredDay = (dayKey: string) =>
        setPreferredDays((prev) =>
            prev.includes(dayKey)
                ? prev.filter((day) => day !== dayKey)
                : [...prev, dayKey].sort()
        );
    const [dateConfirmed, setDateConfirmed] = useState<boolean | null>(null);
    const [savedDateConfirmed, setSavedDateConfirmed] = useState<boolean | null>(null);
    const windowStart = toDay(filmWindow?.est_film_start);
    const windowEnd = toDay(filmWindow?.est_film_end);
    // Only a COMPLETE schedule (every one of film_days picked) is shown to the
    // expert / confirmable — a partial pick is still being worked on by the
    // team (the API rejects user_confirmed until it's complete).
    const pickedDays = getFilmDays(filmWindow);
    const scheduleComplete =
        pickedDays.length > 0 && pickedDays.length === getFilmDaysCount(filmWindow);
    const assignedDays = scheduleComplete ? pickedDays : [];
    const preferredDateCaption = (() => {
        if (savedDateConfirmed === true) {
            return "Locked now that you have confirmed the filming dates";
        }
        if (hasFilmWindow(filmWindow)) {
            return `Select every date you are available to film within ${formatFilmWindow(filmWindow)}`;
        }
        return "Select every date you are available to film";
    })();
    const [unavailableDateRange, setUnavailableDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [existingVideoPath, setExistingVideoPath] = useState("");
    const [locationImages, setLocationImages] = useState<string[]>([]);
    const [uploadingImages, setUploadingImages] = useState(false);
    // The intro video uploads as soon as it is picked (with progress) and the
    // resulting URL lives in existingVideoPath, so Submit never re-uploads and
    // a failed attempt keeps the upload. The sequence guard drops the result
    // of a superseded pick.
    const [videoUploading, setVideoUploading] = useState(false);
    const [videoUploadProgress, setVideoUploadProgress] = useState(0);
    const videoUploadSeq = React.useRef(0);
    const [draftKey, setDraftKey] = useState("");
    const videoInputRef = React.useRef<HTMLInputElement>(null);
    const imageInputRef = React.useRef<HTMLInputElement>(null);
    const [productReceived, setProductReceived] = useState(false);
    const [productReceivedLoading, setProductReceivedLoading] = useState(false);
    const [expertRate, setExpertRate] = useState<number | null>(null);
    const [declineReason, setDeclineReason] = useState("");
    const [decisionMade, setDecisionMade] = useState(false);
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);
    // Opt-ins close once the selection is in pre-production (API optInsClosed).
    // An expert who already opted in keeps their response editable (date
    // confirmation, shipping); everyone else sees the choice closed and can
    // only pass. The API refuses a late I'm-in (423 OptInsClosed) regardless.
    const [optInsClosed, setOptInsClosed] = useState(false);
    const [savedInterested, setSavedInterested] = useState<boolean | null>(null);
    const optInLocked = optInsClosed && savedInterested !== true;
    const [videoPreviewUrl, setVideoPreviewUrl] = useState("");

    useEffect(() => {
        if (!videoFile) {
            setVideoPreviewUrl("");
            return;
        }
        const url = URL.createObjectURL(videoFile);
        setVideoPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [videoFile]);

    const [initialValues, setInitialValues] = useState({
        interested: true,
        interestNotes: "",
        phoneNumber: "",
        willingToTravel: true,
        shippingAddress1: "",
        shippingAddress2: "",
        shippingCity: "",
        shippingState: "",
        shippingZip: "",
        usesProduct: false,
        productExperience: "",
        willingToFilmAtLocation: true,
        filmingAddress: "",
        filmingCity: "",
        filmingState: "",
        filmingZip: ""
    });

    useEffect(() => {
        if (!isAdminView && user && user.msaAgreementSigned === false) {
            toast.error("Please sign your MSA agreement before opting in.");
            navigate("/profile?tab=MSA", { replace: true });
            return;
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, isAdminView]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const url = isAdminView
                ? `/admin/brand-projects/${projectId}/experts/${userId}/confirmation`
                : `/expert/projects/${projectId}/confirmation`;
            const { data } = await axiosInstant.get(url);
            setProject(data.project);
            setProcessProjects(
                Array.isArray(data.processProjects) ? data.processProjects : []
            );
            setFilmWindow(data.filmWindow || null);
            setOptInsClosed(data.optInsClosed === true);

            const vals: any = { ...initialValues };
            if (!isAdminView) {
                vals.phoneNumber = (user as any)?.phoneNumber || "";
            }
            if (data.expertAddress) {
                vals.shippingAddress1 = data.expertAddress.address1 || "";
                vals.shippingAddress2 = data.expertAddress.address2 || "";
                vals.shippingCity = data.expertAddress.city || "";
                vals.shippingState = data.expertAddress.state || "";
                vals.shippingZip = data.expertAddress.zipcode || "";
            }
            // Same rule as shipping: the filming location starts as the
            // address saved on this confirmation, else the profile address,
            // and is stored per project when changed (the profile is never
            // touched). The API resolves which one applies.
            if (data.filmingAddress) {
                vals.filmingAddress = data.filmingAddress.address || "";
                vals.filmingCity = data.filmingAddress.city || "";
                vals.filmingState = data.filmingAddress.state || "";
                vals.filmingZip = data.filmingAddress.zip || "";
            }
            if (data.confirmation) {
                const c = data.confirmation;
                vals.interested = c.interested ?? true;
                setSavedInterested(c.interested === true);
                vals.interestNotes = c.interest_notes || "";
                vals.willingToTravel = c.willing_to_travel ?? true;
                vals.usesProduct = c.uses_product ?? false;
                vals.productExperience = c.product_experience || "";
                vals.willingToFilmAtLocation = c.willing_to_film_at_location ?? true;
                // Older API builds don't send filmingAddress: fall back to
                // the saved columns.
                if (!data.filmingAddress) {
                    vals.filmingAddress = c.filming_address || "";
                    vals.filmingCity = c.filming_city || "";
                    vals.filmingState = c.filming_state || "";
                    vals.filmingZip = c.filming_zip || "";
                }
                const savedConfirmation =
                    c.user_confirmed === null || c.user_confirmed === undefined
                        ? null
                        : !!c.user_confirmed;
                setDateConfirmed(savedConfirmation);
                setSavedDateConfirmed(savedConfirmation);
                if (c.preferred_dates?.length >= 1) setPreferredDays(toDayList(c.preferred_dates));
                if (c.unavailable_dates?.length >= 2)
                    setUnavailableDateRange([dayjs(c.unavailable_dates[0]), dayjs(c.unavailable_dates[1])]);
                if (c.video_path) setExistingVideoPath(c.video_path);
                if (c.location_images) setLocationImages(c.location_images);
                if (c.decline_reason) setDeclineReason(c.decline_reason);
                setProductReceived(!!c.product_received);
                if (c.rate !== null && c.rate !== undefined && c.rate !== "") {
                    setExpertRate(parseFloat(c.rate));
                }
                setDecisionMade(true);
                setAlreadySubmitted(c.confirmation_status === 'submitted');
            }
            if (!isAdminView) {
                const key = draftKeyFor((user as any)?.userid, projectId);
                setDraftKey(key);
                const draft = readDraft(key);
                if (draft) {
                    Object.assign(vals, draft.values || {});
                    if (Array.isArray(draft.preferredDays)) {
                        setPreferredDays(draft.preferredDays);
                    }
                    setUnavailableDateRange([
                        draft.unavailableDateRange?.[0] ? dayjs(draft.unavailableDateRange[0]) : null,
                        draft.unavailableDateRange?.[1] ? dayjs(draft.unavailableDateRange[1]) : null
                    ]);
                    // A date confirmation the server already holds is locked
                    // in the UI; only an unsaved answer comes from the draft.
                    if (data.confirmation?.user_confirmed !== true) {
                        setDateConfirmed(draft.dateConfirmed ?? null);
                    }
                    setDeclineReason(draft.declineReason || "");
                    setLocationImages(Array.isArray(draft.locationImages) ? draft.locationImages : []);
                    setExistingVideoPath(draft.videoPath || "");
                    setVideoFile(null);
                    setDecisionMade(true);
                    toast.success("Restored your unsaved answers from this device.", { duration: 4000 });
                }
            }
            setInitialValues(vals);
        } catch (error) {
            toast.error("Failed to load project details.");
        } finally {
            setLoading(false);
        }
    };

    const handleFormSubmit = async (values: any, actions: any) => {
        if (values.interested && optInLocked) {
            toast.error(submitErrorMessage({ response: { data: "OptInsClosed" } }, filmWindow), {
                duration: 6000
            });
            actions.setSubmitting(false);
            return;
        }
        if (values.interested && !scheduleComplete && preferredDays.length === 0) {
            toast.error("Select at least one date you are available to film.");
            actions.setSubmitting(false);
            return;
        }
        if (
            values.interested &&
            !scheduleComplete &&
            hasFilmWindow(filmWindow) &&
            preferredDays.some((day) => !isWithinFilmWindow(day, filmWindow))
        ) {
            toast.error(`Pick days within ${formatFilmWindow(filmWindow)}.`);
            actions.setSubmitting(false);
            return;
        }
        if (!values.interested && !declineReason) {
            toast.error("Please select a reason for declining.");
            actions.setSubmitting(false);
            return;
        }
        if (videoUploading) {
            toast.error("Your introduction video is still uploading — give it a moment.");
            actions.setSubmitting(false);
            return;
        }
        if (uploadingImages) {
            toast.error("Your location photos are still uploading — give it a moment.");
            actions.setSubmitting(false);
            return;
        }
        // Staff answering on an expert's behalf cannot upload the expert's
        // intro video, so the API exempts them — the form must match.
        if (values.interested && !existingVideoPath && !isAdminView) {
            toast.error("Upload an introduction video to continue.");
            actions.setSubmitting(false);
            return;
        }
        const newPhone = values.phoneNumber?.trim() || "";
        const currentPhone = (user as any)?.phoneNumber?.trim?.() || "";
        if (!isAdminView && values.interested && newPhone !== currentPhone) {
            try {
                await axiosInstant.patch(updateMyInfo(), { phoneNumber: newPhone || null });
                await updateUserInfo();
            } catch (err: any) {
                toast.error(
                    err?.code === "ECONNABORTED"
                        ? TIMEOUT_MESSAGE
                        : "Couldn't save your phone number. Please try again.",
                    { duration: 6000 }
                );
                actions.setSubmitting(false);
                return;
            }
        }
        try {
            // Already uploaded when it was picked (or restored from the draft).
            const videoPath = existingVideoPath || null;
            const submitUrl = isAdminView
                ? `/admin/brand-projects/${projectId}/experts/${userId}/confirmation`
                : `/expert/projects/${projectId}/confirmation`;
            await axiosInstant.post(submitUrl, {
                interested: values.interested,
                interest_notes: values.interestNotes || null,
                decline_reason: !values.interested ? declineReason : null,
                preferred_dates: preferredDays.length > 0 ? preferredDays : null,
                ...(values.interested && scheduleComplete && dateConfirmed !== null
                    ? { user_confirmed: dateConfirmed }
                    : {}),
                unavailable_dates:
                    unavailableDateRange[0] && unavailableDateRange[1]
                        ? [unavailableDateRange[0].format("YYYY-MM-DD"), unavailableDateRange[1].format("YYYY-MM-DD")]
                        : null,
                willing_to_travel: values.willingToTravel,
                ...(values.interested
                    ? {
                        shipping_address1: values.shippingAddress1,
                        shipping_address2: values.shippingAddress2,
                        shipping_city: values.shippingCity,
                        shipping_state: values.shippingState,
                        shipping_zipcode: values.shippingZip,
                        willing_to_film_at_location: values.willingToFilmAtLocation,
                        // Sent whichever way the toggle is set: a NO keeps the
                        // address so a later YES starts from it again.
                        filming_address: values.filmingAddress || null,
                        filming_city: values.filmingCity || null,
                        filming_state: values.filmingState || null,
                        filming_zip: values.filmingZip || null
                    }
                    : {}),
                uses_product: values.usesProduct,
                product_experience: values.productExperience || null,
                video_path: videoPath,
                location_images: locationImages.length > 0 ? locationImages : null
            });
            if (draftKey) clearDraft(draftKey);
            toast.success(
                isAdminView
                    ? "Saved the expert's response."
                    : "Confirmed project details successfully!"
            );
            navigate(isAdminView ? `/video-production/projects/${projectId}` : "/expert/projects");
        } catch (error: any) {
            const errData = error?.response?.data;
            const errCode = typeof errData === "string" ? errData : errData?.error;
            if (error?.response?.status === 403 && errCode === "MsaNotSigned") {
                if (isAdminView) {
                    // Staff cannot send or sign an expert's MSA (every DocuSign
                    // action is self-only), so there is nowhere to redirect.
                    toast.error(
                        "This expert has not signed their MSA. They need to sign it from their profile before they can be opted in.",
                        { duration: 6000 }
                    );
                    return;
                }
                toast.error(
                    "Your MSA agreement is not signed yet. Please sign it before opting in — redirecting you to the MSA tab.",
                    { duration: 4500 }
                );
                navigate("/profile?tab=MSA");
                return;
            }
            toast.error(submitErrorMessage(error, filmWindow), { duration: 6000 });
        } finally {
            actions.setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    backgroundColor: T.canvas,
                    minHeight: "100vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                }}
            >
                <DotLottleLoader fill={false} />
            </Box>
        );
    }

    const brandObj = project?.brands?.find((b: any) => !b.rmn) || project?.brands?.[0];
    const brandName = brandObj?.name || "";
    const rawLogo = brandObj?.logo || brandObj?.cover_image || "";
    const brandLogo = rawLogo && !rawLogo.includes(" ") ? rawLogo : "";
    const productWithImage = project?.products?.find((p: any) => p.image);
    const brandImage = brandLogo || productWithImage?.image || "";
    const displayedRate = Number(expertRate ?? project?.rate);
    const showRate = Number.isFinite(displayedRate) && displayedRate !== 0;
    const productTitle = project?.products?.map((p: any) => p.title).join(", ") || "";
    const projectCode = `#${project?.id || projectId}`;
    const expertUser = user as any;
    const goToProfileContact = () =>
        navigate(`/profile?tab=${encodeURIComponent(ProFormTabs.ContactInfo)}`);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={isAdminView ? undefined : contactValidationSchema}
            onSubmit={handleFormSubmit}
            enableReinitialize
        >
            {({ values, setFieldValue, isSubmitting }) => {
                let submitLabel = "Submit";
                if (isSubmitting) submitLabel = "Submitting...";
                else if (videoUploading) submitLabel = "Uploading video...";
                const undecidedHint = optInLocked
                    ? "Opt-ins are closed for this project. You can still pass above."
                    : "Select Pass or I'm in above to continue.";
                return (
                <Form>
                    {!isAdminView && (
                        <DraftPersist
                            draftKey={draftKey}
                            enabled={!!draftKey}
                            extra={{
                                preferredDays,
                                unavailableDateRange: [
                                    unavailableDateRange[0] ? unavailableDateRange[0].format("YYYY-MM-DD") : null,
                                    unavailableDateRange[1] ? unavailableDateRange[1].format("YYYY-MM-DD") : null
                                ],
                                dateConfirmed,
                                declineReason,
                                locationImages,
                                videoPath: existingVideoPath
                            }}
                        />
                    )}
                    <Box
                        sx={{
                            backgroundColor: T.canvas,
                            minHeight: "100vh",
                            py: "28px",
                            px: { xs: "16px", sm: "32px" },
                            pb: "56px"
                        }}
                    >
                        <Box sx={{ maxWidth: "800px", mx: "auto" }}>
                            {/* ── Page Header ── */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    gap: 2,
                                    mb: 2.5
                                }}
                            >
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: "CircularStd",
                                            fontWeight: 900,
                                            fontSize: "30px",
                                            color: T.text,
                                            lineHeight: 1.15,
                                            letterSpacing: "-0.01em",
                                            mt: "2px"
                                        }}
                                    >
                                        {isAdminView ? "Confirmation Details" : "Project Confirmation"}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mt: 0.75,
                                            color: T.textSoft,
                                            fontSize: "13px",
                                            fontFamily: "CircularStd"
                                        }}
                                    >
                                        <span>{projectCode}</span>
                                    </Box>
                                </Box>

                                {/* Status pill */}
                                {alreadySubmitted && (
                                    <Box sx={{ mt: 1, flexShrink: 0 }}>
                                        <Box
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 0.75,
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: "999px",
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                fontFamily: "CircularStd",
                                                backgroundColor: values.interested ? T.acceptBg : T.declineBg,
                                                color: values.interested ? T.acceptText : T.declineText
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: "50%",
                                                    backgroundColor: "currentColor",
                                                    flexShrink: 0
                                                }}
                                            />
                                            {values.interested ? "Opted-In" : "Opted-Out"}
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            {/* ── Content Stack ── */}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                {!isAdminView && <ContactReminderBanner />}

                                {/* ── Project Brief Card ── */}
                                <Box sx={cardSx}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            mb: 2
                                        }}
                                    >
                                        <Box sx={eyebrowSx}>Project Brief</Box>
                                        <Box
                                            sx={{
                                                backgroundColor: T.bronzeDeep,
                                                color: "#fff",
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: "999px",
                                                fontSize: "11px",
                                                fontWeight: 600,
                                                fontFamily: "CircularStd"
                                            }}
                                        >
                                            {projectCode}
                                        </Box>
                                    </Box>

                                    {/* Thumb + info row */}
                                    <Box sx={{ display: "flex", gap: 2, mb: 2.5 }}>
                                        <Box
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                minWidth: 56,
                                                borderRadius: "8px",
                                                backgroundColor: "var(--surface-2)",
                                                overflow: "hidden",
                                                flexShrink: 0,
                                                position: "relative"
                                            }}
                                        >
                                            {brandImage ? (
                                                <img 
                                                    src={brandImage}
                                                    alt="product"
                                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).style.display = "none";
                                                    }}
                                                />
                                            ) : (
                                                <></>
                                            )}
                                            {project?.products?.length > 1 && (
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        bottom: 3,
                                                        right: 3,
                                                        backgroundColor: T.bronzeDeep,
                                                        color: "#fff",
                                                        fontSize: "10px",
                                                        fontWeight: 700,
                                                        borderRadius: "50%",
                                                        width: 18,
                                                        height: 18,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontFamily: "CircularStd"
                                                    }}
                                                >
                                                    {project.products.length}
                                                </Box>
                                            )}
                                            {!brandImage && (
                                                <Box
                                                    sx={{
                                                        width: "100%",
                                                        height: "100%",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        backgroundColor: T.bronzeSoft
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: "16px",
                                                            fontWeight: 700,
                                                            color: "var(--accent-text)",
                                                            fontFamily: "CircularStd"
                                                        }}
                                                    >
                                                        {brandName
                                                            ?.split(" ")
                                                            .map((w: string) => w[0])
                                                            .join("")
                                                            .slice(0, 2)
                                                            .toUpperCase() || "?"}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            {productTitle && (
                                                <Typography
                                                    sx={{
                                                        fontFamily: "CircularStd",
                                                        fontSize: "15px",
                                                        fontWeight: 700,
                                                        color: T.text,
                                                        mb: 1,
                                                        lineHeight: 1.35
                                                    }}
                                                >
                                                    {productTitle}
                                                </Typography>
                                            )}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1.5,
                                                    flexWrap: "wrap"
                                                }}
                                            >
                                                <BrandInlineList brands={project?.brands || []} />
                                                {project?.content_type?.name && (
                                                    <Chip
                                                        label={project.content_type.name}
                                                        size="small"
                                                        sx={{
                                                            color: "var(--text-2)",
                                                            fontFamily: "CircularStd",
                                                            fontSize: "14px",
                                                            fontWeight: 450,
                                                            borderRadius: "24px",
                                                            backgroundColor: "var(--surface-2)",
                                                            p: 1,
                                                            textTransform: "capitalize"
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            {project?.description && (
                                                <Typography
                                                    sx={{
                                                        fontFamily: "CircularStd",
                                                        fontSize: "13px",
                                                        color: T.textSoft,
                                                        mt: 1,
                                                        lineHeight: 1.5
                                                    }}
                                                >
                                                    {project.description}
                                                </Typography>
                                            )}
                                            {processProjects.length > 1 && (
                                                <Box
                                                    sx={{
                                                        mt: 1.5,
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        gap: 1,
                                                        flexWrap: "wrap"
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "CircularStd",
                                                            fontSize: "13px",
                                                            color: T.textSoft,
                                                            lineHeight: 1.6
                                                        }}
                                                    >
                                                        This one response covers all{" "}
                                                        {processProjects.length} videos in this
                                                        engagement:
                                                    </Typography>
                                                    <Box className="flex flex-wrap gap-1.5">
                                                        {processProjects.map((pp) => (
                                                            <Chip
                                                                key={pp.id}
                                                                size="small"
                                                                label={
                                                                    pp.content_type?.name
                                                                        ? `${pp.title || "Untitled"} · ${pp.content_type.name}`
                                                                        : pp.title || "Untitled"
                                                                }
                                                                sx={{
                                                                    color: "#5E5E5E",
                                                                    fontFamily: "CircularStd",
                                                                    fontSize: "12px",
                                                                    fontWeight: 450,
                                                                    borderRadius: "24px",
                                                                    backgroundColor: "#F4F4F4"
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* KV Grid */}
                                    {(showRate ||
                                        project?.film_date ||
                                        project?.estimated_time_on_set ||
                                        project?.filming_location_text) && (
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                                                gap: "12px 20px",
                                                pt: 2,
                                                borderTop: `1px solid ${T.rule}`
                                            }}
                                        >
                                            {showRate && (
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "CircularStd",
                                                            fontSize: "11px",
                                                            color: T.textMute,
                                                            mb: 0.25
                                                        }}
                                                    >
                                                        Compensation
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "CircularStd",
                                                            fontSize: "14px",
                                                            fontWeight: 700,
                                                            color: "var(--accent-text)"
                                                        }}
                                                    >
                                                        ${displayedRate.toFixed(2)}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {project?.film_date && (
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "CircularStd",
                                                            fontSize: "11px",
                                                            color: T.textMute,
                                                            mb: 0.25
                                                        }}
                                                    >
                                                        {assignedDays.length > 1 ? "Filming Dates" : "Filming Date"}
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "CircularStd",
                                                            fontSize: "14px",
                                                            fontWeight: 500,
                                                            color: T.text
                                                        }}
                                                    >
                                                        {assignedDays.length > 0
                                                            ? formatFilmDayList(assignedDays)
                                                            : formatFilmDay(project.film_date)}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {project?.estimated_time_on_set && (
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "CircularStd",
                                                            fontSize: "11px",
                                                            color: T.textMute,
                                                            mb: 0.25
                                                        }}
                                                    >
                                                        Time on Set
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "CircularStd",
                                                            fontSize: "14px",
                                                            fontWeight: 500,
                                                            color: T.text
                                                        }}
                                                    >
                                                        {project.estimated_time_on_set}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {project?.filming_location_text && (
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "CircularStd",
                                                            fontSize: "11px",
                                                            color: T.textMute,
                                                            mb: 0.25
                                                        }}
                                                    >
                                                        Location
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "CircularStd",
                                                            fontSize: "14px",
                                                            fontWeight: 500,
                                                            color: T.text
                                                        }}
                                                    >
                                                        {project.filming_location_text}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>

                                {/* ── Decision Section ── */}
                                <Box>
                                    <Box sx={{ ...eyebrowSx, mb: 0.75 }}>Your decision</Box>
                                    <Typography
                                        sx={{
                                            fontFamily: "CircularStd",
                                            fontWeight: 700,
                                            fontSize: "20px",
                                            color: T.text,
                                            lineHeight: 1.3,
                                            mb: 0.75
                                        }}
                                    >
                                        Is this project a fit for you?
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: "CircularStd",
                                            fontSize: "13px",
                                            color: T.textSoft,
                                            mb: 2.5,
                                            lineHeight: 1.5
                                        }}
                                    >
                                        Confirm you're interested and have no conflicts with {brandName || "this brand"}
                                        . You'll add availability and shipping details below.
                                    </Typography>

                                    {optInLocked && (
                                        <Box
                                            sx={{
                                                mb: 2,
                                                px: 2,
                                                py: 1.5,
                                                borderRadius: "10px",
                                                border: `1px solid ${T.rule}`,
                                                backgroundColor: "var(--surface-2)",
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: 1.25
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    mt: "3px",
                                                    px: 1,
                                                    py: 0.25,
                                                    borderRadius: "999px",
                                                    fontSize: "11px",
                                                    fontWeight: 700,
                                                    fontFamily: "CircularStd",
                                                    letterSpacing: "0.04em",
                                                    textTransform: "uppercase",
                                                    backgroundColor: T.declineBg,
                                                    color: T.declineText,
                                                    flexShrink: 0
                                                }}
                                            >
                                                Opt-ins closed
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontFamily: "CircularStd",
                                                    fontSize: "13px",
                                                    color: T.textSoft,
                                                    lineHeight: 1.5
                                                }}
                                            >
                                                {isAdminView
                                                    ? "This selection is in pre-production, so opt-ins are closed. To add this expert, reopen the selection first."
                                                    : "This project is already in pre-production, so opt-ins are closed. You can still let us know if you'd like to pass. Questions? Contact your producer."}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Box sx={{ display: "flex", gap: 2 }}>
                                        {/* Pass button */}
                                        <ButtonBase
                                            disableRipple={readOnly}
                                            onClick={() => {
                                                if (!readOnly) {
                                                    setFieldValue("interested", false);
                                                    setDecisionMade(true);
                                                }
                                            }}
                                            sx={{
                                                flex: 1,
                                                p: 2,
                                                borderRadius: "12px",
                                                border: "1.5px solid",
                                                borderColor: decisionMade && !values.interested ? "#E8C5C0" : T.rule,
                                                backgroundColor:
                                                    decisionMade && !values.interested ? T.declineBg : T.surface,
                                                cursor: readOnly ? "default" : "pointer",
                                                textAlign: "left",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                transition: "all 120ms ease",
                                                "&:hover": readOnly
                                                    ? {}
                                                    : {
                                                          borderColor:
                                                              decisionMade && !values.interested
                                                                  ? "#E0A9A1"
                                                                  : T.ruleStrong
                                                      }
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    // SVG presentation attrs can't resolve var(); set it via style
                                                    style={{
                                                        stroke:
                                                            decisionMade && !values.interested
                                                                ? T.declineText
                                                                : T.textSoft
                                                    }}
                                                    strokeWidth="2.2"
                                                >
                                                    <path d="M6 6l12 12M18 6 6 18" />
                                                </svg>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "CircularStd",
                                                        fontWeight: 700,
                                                        fontSize: "14px",
                                                        color:
                                                            decisionMade && !values.interested ? T.declineText : T.text
                                                    }}
                                                >
                                                    Pass
                                                </Typography>
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontFamily: "CircularStd",
                                                    fontSize: "12px",
                                                    color:
                                                        decisionMade && !values.interested ? T.declineText : T.textMute,
                                                    pl: "24px"
                                                }}
                                            >
                                                Not the right fit
                                            </Typography>
                                        </ButtonBase>

                                        {/* I'm in button — closed (not clickable) once the
                                            selection is in pre-production */}
                                        <ButtonBase
                                            disableRipple={readOnly || optInLocked}
                                            disabled={optInLocked}
                                            onClick={() => {
                                                if (!readOnly && !optInLocked) {
                                                    setFieldValue("interested", true);
                                                    setDecisionMade(true);
                                                }
                                            }}
                                            sx={{
                                                flex: 1,
                                                p: 2,
                                                borderRadius: "12px",
                                                border: "1.5px solid",
                                                borderColor: decisionMade && values.interested ? "#C8A86B" : T.rule,
                                                backgroundColor:
                                                    decisionMade && values.interested ? T.bronzeSoft : T.surface,
                                                cursor: readOnly || optInLocked ? "default" : "pointer",
                                                opacity: optInLocked ? 0.55 : 1,
                                                textAlign: "left",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                transition: "all 120ms ease",
                                                "&:hover": readOnly || optInLocked
                                                    ? {}
                                                    : {
                                                          borderColor:
                                                              decisionMade && values.interested
                                                                  ? "#B8955A"
                                                                  : T.ruleStrong
                                                      }
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    // SVG presentation attrs can't resolve var(); set it via style
                                                    style={{
                                                        stroke:
                                                            decisionMade && values.interested
                                                                ? "var(--accent-text)"
                                                                : T.textSoft
                                                    }}
                                                    strokeWidth="2.4"
                                                >
                                                    <path d="M5 12l5 5 9-11" />
                                                </svg>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "CircularStd",
                                                        fontWeight: 700,
                                                        fontSize: "14px",
                                                        color:
                                                            decisionMade && values.interested
                                                                ? "var(--accent-text)"
                                                                : T.text
                                                    }}
                                                >
                                                    I'm in
                                                </Typography>
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontFamily: "CircularStd",
                                                    fontSize: "12px",
                                                    color:
                                                        decisionMade && values.interested
                                                            ? "var(--accent-text)"
                                                            : T.textMute,
                                                    pl: "24px"
                                                }}
                                            >
                                                {optInLocked ? "Opt-ins are closed" : "No conflicts, let's go"}
                                            </Typography>
                                        </ButtonBase>
                                    </Box>
                                </Box>

                                {/* ── Contact Information (always visible to experts) ── */}
                                {!isAdminView && (
                                    <Box sx={cardSx}>
                                        <Typography
                                            sx={{
                                                fontFamily: "CircularStd",
                                                fontWeight: 700,
                                                fontSize: "14px",
                                                mb: 0.5
                                            }}
                                        >
                                            Contact Information
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: "CircularStd",
                                                fontSize: "12px",
                                                color: T.textSoft,
                                                mb: 2,
                                                pb: 1.5,
                                                borderBottom: `1px solid ${T.rule}`
                                            }}
                                        >
                                            Make sure your contact details are up to date. Fulfillment uses these to
                                            reach you after selection.
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <CustomFieldLabel>Email</CustomFieldLabel>
                                                <Box
                                                    sx={{
                                                        mt: 1,
                                                        height: "50px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        px: 2,
                                                        borderRadius: "12px",
                                                        backgroundColor: "var(--surface-2)",
                                                        color: T.textSoft,
                                                        fontFamily: "CircularStd",
                                                        fontSize: "14px",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap"
                                                    }}
                                                >
                                                    {expertUser?.emailAddress || "—"}
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontFamily: "CircularStd",
                                                        fontSize: "11px",
                                                        color: T.textMute,
                                                        mt: 0.5
                                                    }}
                                                >
                                                    To change your email, update your{" "}
                                                    <Box
                                                        component="span"
                                                        onClick={goToProfileContact}
                                                        sx={{
                                                            color: "var(--accent-text)",
                                                            cursor: "pointer",
                                                            textDecoration: "underline"
                                                        }}
                                                    >
                                                        profile
                                                    </Box>
                                                    .
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <CustomFieldLabel required={values.interested}>
                                                    Phone Number
                                                </CustomFieldLabel>
                                                <Box sx={{ mt: 1 }}>
                                                    <CustomFieldText
                                                        name="phoneNumber"
                                                        fullWidth
                                                        placeholder="(123) 456-7890"
                                                    />
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}

                                {/* ── Conditional sections ── */}
                                {decisionMade && (
                                    <fieldset
                                        disabled={readOnly}
                                        style={{
                                            border: 0,
                                            padding: 0,
                                            margin: 0,
                                            ...(readOnly ? { pointerEvents: "none", opacity: 0.85 } : {})
                                        }}
                                    >
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            {/* OptOut card */}
                                            {!values.interested && (
                                                <Box sx={cardSx}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "CircularStd",
                                                            fontWeight: 700,
                                                            fontSize: "15px",
                                                            color: T.text,
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        Help us route better next time
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "CircularStd",
                                                            fontSize: "12px",
                                                            color: T.textSoft,
                                                            mb: 3
                                                        }}
                                                    >
                                                        One reason and an optional note helps us understand why this
                                                        wasn't a fit.
                                                    </Typography>

                                                    {/* Reason */}
                                                    <Box sx={{ mb: 3 }}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 0.5,
                                                                mb: 1.25
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: "CircularStd",
                                                                    fontSize: "12px",
                                                                    fontWeight: 700,
                                                                    color: T.text
                                                                }}
                                                            >
                                                                Reason
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: "CircularStd",
                                                                    fontSize: "12px",
                                                                    color: T.declineText
                                                                }}
                                                            >
                                                                *
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                            {DECLINE_REASONS.filter(
                                                                (r) => r !== "Schedule conflict" || !!project?.film_date
                                                            ).map((reason) => (
                                                                <ButtonBase
                                                                    key={reason}
                                                                    onClick={() =>
                                                                        setDeclineReason(
                                                                            declineReason === reason ? "" : reason
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        px: 1.5,
                                                                        py: 0.75,
                                                                        borderRadius: "999px",
                                                                        border: "1px solid",
                                                                        borderColor:
                                                                            declineReason === reason
                                                                                ? T.bronze
                                                                                : T.ruleStrong,
                                                                        backgroundColor:
                                                                            declineReason === reason
                                                                                ? T.bronzeSoft
                                                                                : T.surface,
                                                                        color:
                                                                            declineReason === reason
                                                                                ? "var(--accent-text)"
                                                                                : T.text,
                                                                        fontSize: "12px",
                                                                        fontWeight:
                                                                            declineReason === reason ? 600 : 400,
                                                                        fontFamily: "CircularStd",
                                                                        transition: "all 120ms ease",
                                                                        "&:hover": { borderColor: T.bronze }
                                                                    }}
                                                                >
                                                                    {reason}
                                                                </ButtonBase>
                                                            ))}
                                                        </Box>
                                                    </Box>

                                                    {/* Note */}
                                                    <Box>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "baseline",
                                                                gap: 1,
                                                                mb: 0.75
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: "CircularStd",
                                                                    fontSize: "12px",
                                                                    fontWeight: 700,
                                                                    color: T.text
                                                                }}
                                                            >
                                                                Note
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: "CircularStd",
                                                                    fontSize: "11px",
                                                                    color: T.textMute
                                                                }}
                                                            >
                                                                Optional · visible to your account team
                                                            </Typography>
                                                        </Box>
                                                        <CustomTextArea
                                                            name="interestNotes"
                                                            label=""
                                                            placeholder="e.g. I'll be filming with a competing brand next month, can revisit in Q3."
                                                            rows={3}
                                                        />
                                                    </Box>
                                                </Box>
                                            )}

                                            {/* OptIn sections */}
                                            {values.interested && (
                                                <>
                                                    {/* Product received (if shipped) */}
                                                    {(project?.expert_shipping_status === "Shipped" ||
                                                        project?.expert_shipping_status === "Received") && (
                                                        <Box sx={cardSx}>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: "CircularStd",
                                                                    fontWeight: 700,
                                                                    fontSize: "14px",
                                                                    mb: 0.5
                                                                }}
                                                            >
                                                                Product Received
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: "CircularStd",
                                                                    fontSize: "13px",
                                                                    color: T.textSoft,
                                                                    mb: 2
                                                                }}
                                                            >
                                                                Please confirm when you have received the product for
                                                                this project.
                                                            </Typography>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        checked={productReceived}
                                                                        disabled={productReceivedLoading}
                                                                        onChange={async (e) => {
                                                                            const value = e.target.checked;
                                                                            try {
                                                                                setProductReceivedLoading(true);
                                                                                await axiosInstant.patch(
                                                                                    `/expert/projects/${projectId}/confirmation/product-received`,
                                                                                    { product_received: value }
                                                                                );
                                                                                setProductReceived(value);
                                                                                toast.success(
                                                                                    value
                                                                                        ? "Product marked as received!"
                                                                                        : "Product receipt status updated."
                                                                                );
                                                                            } catch (err) {
                                                                                toast.error(
                                                                                    "Failed to update product received status."
                                                                                );
                                                                            } finally {
                                                                                setProductReceivedLoading(false);
                                                                            }
                                                                        }}
                                                                    />
                                                                }
                                                                label={
                                                                    productReceived
                                                                        ? "Yes, I have received the product"
                                                                        : "Not yet received"
                                                                }
                                                            />
                                                        </Box>
                                                    )}

                                                    {/* Availability */}
                                                    <Box sx={cardSx}>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "CircularStd",
                                                                fontWeight: 700,
                                                                fontSize: "14px",
                                                                mb: 2,
                                                                pb: 1.5,
                                                                borderBottom: `1px solid ${T.rule}`
                                                            }}
                                                        >
                                                            Availability
                                                        </Typography>
                                                        <FilmDateConfirmField
                                                            filmDate={scheduleComplete ? filmWindow?.film_date : null}
                                                            filmDays={assignedDays}
                                                            value={dateConfirmed}
                                                            onChange={setDateConfirmed}
                                                            locked={savedDateConfirmed === true}
                                                        />
                                                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-us">
                                                    <Grid container spacing={3}>
                                                            {!scheduleComplete && (
                                                            <Grid item xs={12} md={6}>
                                                                <CustomFieldLabel required>
                                                                    Preferred Filming Dates
                                                                    {hasFilmWindow(filmWindow) && (
                                                                        <span className="ml-1 font-normal text-darkgray1">
                                                                            ({formatFilmWindow(filmWindow)})
                                                                        </span>
                                                                    )}
                                                                </CustomFieldLabel>
                                                                <Box sx={{ mt: 1 }}>
                                                                    <ExpertCalendar
                                                                        bare
                                                                        label=""
                                                                        selectedDays={preferredDays}
                                                                        onToggleDay={togglePreferredDay}
                                                                        disabled={savedDateConfirmed === true}
                                                                        minDate={windowStart || dayjs()}
                                                                        maxDate={
                                                                            windowEnd ||
                                                                            dayjs().add(2, "month").endOf("month")
                                                                        }
                                                                        placeholder="Select your available dates"
                                                                        caption={preferredDateCaption}
                                                                    />
                                                                </Box>
                                                            </Grid>
                                                            )}
                                                            <Grid item xs={12} md={scheduleComplete ? 12 : 6}>
                                                                <Grid container spacing={3}>
                                                                    <Grid item xs={12} md={scheduleComplete ? 6 : 12}>
                                                                        <CustomFieldLabel required>
                                                                            Willingness to Travel
                                                                        </CustomFieldLabel>
                                                                        <Box sx={{ mt: 1 }}>
                                                                            <FormControlLabel
                                                                                control={
                                                                                    <Switch
                                                                                        checked={values.willingToTravel}
                                                                                        onChange={(e) =>
                                                                                            setFieldValue(
                                                                                                "willingToTravel",
                                                                                                e.target.checked
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                }
                                                                                label={
                                                                                    values.willingToTravel
                                                                                        ? "Yes, willing to travel"
                                                                                        : "No"
                                                                                }
                                                                            />
                                                                            {values.willingToTravel && project?.travel_expenses_covered && (
                                                                                <Typography
                                                                                    sx={{
                                                                                        fontFamily: "CircularStd",
                                                                                        fontSize: "12px",
                                                                                        color: T.textSoft,
                                                                                        mt: 0.5
                                                                                    }}
                                                                                >
                                                                                    Travel expenses will be covered.
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={12} md={scheduleComplete ? 6 : 12}>
                                                                        <CustomFieldLabel>Product Experience</CustomFieldLabel>
                                                                        <Box sx={{ mt: 1 }}>
                                                                            <FormControlLabel
                                                                                control={
                                                                                    <Switch
                                                                                        checked={values.usesProduct}
                                                                                        onChange={(e) =>
                                                                                            setFieldValue(
                                                                                                "usesProduct",
                                                                                                e.target.checked
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                }
                                                                                label={
                                                                                    values.usesProduct
                                                                                        ? "Currently use this brand"
                                                                                        : "Not currently using"
                                                                                }
                                                                            />
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={12}>
                                                                        <CustomTextArea
                                                                            name="productExperience"
                                                                            label=""
                                                                            placeholder="Share your experience with this brand's products..."
                                                                            rows={3}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </LocalizationProvider>
                                                    </Box>

                                                    {/* Shipping Address */}
                                                    <Box sx={cardSx}>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "CircularStd",
                                                                fontWeight: 700,
                                                                fontSize: "14px",
                                                                mb: 2,
                                                                pb: 1.5,
                                                                borderBottom: `1px solid ${T.rule}`
                                                            }}
                                                        >
                                                            Shipping Address
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: "CircularStd",
                                                                        fontSize: "12px",
                                                                        color: T.textSoft,
                                                                        mb: 0.5
                                                                    }}
                                                                >
                                                                    Address Line 1
                                                                </Typography>
                                                                <CustomFieldText
                                                                    name="shippingAddress1"
                                                                    fullWidth
                                                                    placeholder="Street Address"
                                                                />
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: "CircularStd",
                                                                        fontSize: "12px",
                                                                        color: T.textSoft,
                                                                        mb: 0.5
                                                                    }}
                                                                >
                                                                    Address Line 2
                                                                </Typography>
                                                                <CustomFieldText
                                                                    name="shippingAddress2"
                                                                    fullWidth
                                                                    placeholder="Apt, Suite, etc. (optional)"
                                                                />
                                                            </Grid>
                                                            <Grid item xs={5}>
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: "CircularStd",
                                                                        fontSize: "12px",
                                                                        color: T.textSoft,
                                                                        mb: 0.5
                                                                    }}
                                                                >
                                                                    City
                                                                </Typography>
                                                                <CustomFieldText
                                                                    name="shippingCity"
                                                                    fullWidth
                                                                    placeholder="City"
                                                                />
                                                            </Grid>
                                                            <Grid item xs={4}>
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: "CircularStd",
                                                                        fontSize: "12px",
                                                                        color: T.textSoft,
                                                                        mb: 0.5
                                                                    }}
                                                                >
                                                                    State
                                                                </Typography>
                                                                <CustomSelectField
                                                                    name="shippingState"
                                                                    label=""
                                                                    noLabel
                                                                    placeholder="State"
                                                                    options={statesList}
                                                                    required={false}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={3}>
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: "CircularStd",
                                                                        fontSize: "12px",
                                                                        color: T.textSoft,
                                                                        mb: 0.5
                                                                    }}
                                                                >
                                                                    ZIP Code
                                                                </Typography>
                                                                <CustomFieldText
                                                                    name="shippingZip"
                                                                    fullWidth
                                                                    placeholder="Zip"
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Box>

                                                    {/* Introduction Video */}
                                                    <Box sx={cardSx}>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "CircularStd",
                                                                fontWeight: 700,
                                                                fontSize: "14px",
                                                                mb: 0.5
                                                            }}
                                                        >
                                                            Introduction Video
                                                            {values.interested && (
                                                                <Box
                                                                    component="span"
                                                                    sx={{ color: "#D32F2F", ml: 0.4 }}
                                                                >
                                                                    *
                                                                </Box>
                                                            )}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "CircularStd",
                                                                fontSize: "12px",
                                                                color: T.textSoft,
                                                                mb: 2
                                                            }}
                                                        >
                                                            Upload a brief video about your experience or why you want
                                                            to work with this brand.
                                                            {values.interested && " Required to opt in."}
                                                        </Typography>
                                                        <input
                                                            ref={videoInputRef}
                                                            type="file"
                                                            accept=".mp4,.mov,.webm"
                                                            style={{ display: "none" }}
                                                            onChange={(e) => {
                                                                const input = e.target;
                                                                const f = input.files?.[0];
                                                                // Reset so picking the same file again re-fires.
                                                                input.value = "";
                                                                if (!f) return;
                                                                if (f.size > 200000000) {
                                                                    toast.error(
                                                                        "File exceeds maximum upload size of 200MB."
                                                                    );
                                                                    return;
                                                                }
                                                                const seq = ++videoUploadSeq.current;
                                                                setVideoFile(f);
                                                                setExistingVideoPath("");
                                                                setVideoUploading(true);
                                                                setVideoUploadProgress(0);
                                                                uploadVideo(f, "confirmation", true, (pct: number) => {
                                                                    if (seq === videoUploadSeq.current)
                                                                        setVideoUploadProgress(pct);
                                                                }).then((url) => {
                                                                    if (seq !== videoUploadSeq.current) return;
                                                                    setVideoUploading(false);
                                                                    if (url) {
                                                                        setExistingVideoPath(url as string);
                                                                    } else {
                                                                        setVideoFile(null);
                                                                        toast.error(
                                                                            "Video upload failed. Check your connection and try again.",
                                                                            { duration: 6000 }
                                                                        );
                                                                    }
                                                                });
                                                            }}
                                                        />
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 2,
                                                                overflow: "hidden",
                                                                backgroundColor: "var(--surface-2)",
                                                                borderRadius: "10px",
                                                                p: 1.5,
                                                                border: `1px solid ${T.rule}`
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 48,
                                                                    height: 48,
                                                                    minWidth: 48,
                                                                    borderRadius: "8px",
                                                                    backgroundColor: "var(--track)",
                                                                    display: "flex",
                                                                    justifyContent: "center",
                                                                    alignItems: "center",
                                                                    cursor: "pointer",
                                                                    overflow: "hidden"
                                                                }}
                                                                onClick={() => videoInputRef.current?.click()}
                                                            >
                                                                {videoFile ? (
                                                                    <video
                                                                        src={videoPreviewUrl}
                                                                        style={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            objectFit: "cover"
                                                                        }}
                                                                    />
                                                                ) : existingVideoPath ? (
                                                                    <video
                                                                        src={existingVideoPath}
                                                                        style={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            objectFit: "cover"
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <VideocamIcon
                                                                        sx={{ fontSize: 24, color: "var(--muted)" }}
                                                                    />
                                                                )}
                                                            </Box>
                                                            <Box sx={{ flex: 1, width: 0, minWidth: 0 }}>
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: "CircularStd",
                                                                        fontSize: "13px",
                                                                        fontWeight: 500,
                                                                        color: T.text,
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap"
                                                                    }}
                                                                >
                                                                    {videoFile
                                                                        ? videoFile.name
                                                                        : existingVideoPath
                                                                        ? "intro_video.mp4"
                                                                        : "No video selected"}
                                                                </Typography>
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: "CircularStd",
                                                                        fontSize: "11px",
                                                                        color: T.textMute
                                                                    }}
                                                                >
                                                                    Recommended: 30–60 sec · 1920×1080 / 1080×1920
                                                                </Typography>
                                                            </Box>
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    gap: 1,
                                                                    flexShrink: 0
                                                                }}
                                                            >
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    sx={{
                                                                        textTransform: "none",
                                                                        borderRadius: "8px",
                                                                        fontSize: "12px",
                                                                        px: 2,
                                                                        backgroundColor: T.bronze,
                                                                        "&:hover": { backgroundColor: T.bronzeDeep }
                                                                    }}
                                                                    onClick={() => videoInputRef.current?.click()}
                                                                >
                                                                    Upload video
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    disabled={!videoFile && !existingVideoPath}
                                                                    sx={{
                                                                        textTransform: "none",
                                                                        borderRadius: "8px",
                                                                        fontSize: "12px",
                                                                        px: 2,
                                                                        color: "var(--text-2)",
                                                                        backgroundColor: "var(--track)",
                                                                        visibility:
                                                                            videoFile || existingVideoPath
                                                                                ? "visible"
                                                                                : "hidden"
                                                                    }}
                                                                    onClick={() => {
                                                                        videoUploadSeq.current += 1;
                                                                        setVideoUploading(false);
                                                                        setVideoFile(null);
                                                                        setExistingVideoPath("");
                                                                    }}
                                                                >
                                                                    Delete
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{ mt: 1.5, minHeight: 32 }}>
                                                            {videoUploading && (
                                                                <>
                                                                    <LinearProgress
                                                                        variant="determinate"
                                                                        value={videoUploadProgress}
                                                                        sx={{ borderRadius: 4, height: 6 }}
                                                                    />
                                                                    <Typography
                                                                        sx={{
                                                                            fontFamily: "CircularStd",
                                                                            fontSize: "11px",
                                                                            color: T.textMute,
                                                                            mt: 0.5
                                                                        }}
                                                                    >
                                                                        Uploading video… {videoUploadProgress}%
                                                                    </Typography>
                                                                </>
                                                            )}
                                                            {!videoUploading && videoFile && existingVideoPath && (
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: "CircularStd",
                                                                        fontSize: "11px",
                                                                        color: T.acceptText
                                                                    }}
                                                                >
                                                                    Video uploaded. It stays attached even if you reload.
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>

                                                    {/* Film at Location */}
                                                    <Box sx={cardSx}>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "CircularStd",
                                                                fontWeight: 700,
                                                                fontSize: "14px",
                                                                mb: 0.5
                                                            }}
                                                        >
                                                            Film at Your Location
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "CircularStd",
                                                                fontSize: "12px",
                                                                color: T.textSoft,
                                                                mb: 1.5,
                                                                lineHeight: 1.5
                                                            }}
                                                        >
                                                            Are you willing to film where the product is naturally used
                                                            (eg your home, office, or workspace)?
                                                        </Typography>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={values.willingToFilmAtLocation}
                                                                    onChange={(e) =>
                                                                        setFieldValue(
                                                                            "willingToFilmAtLocation",
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label={
                                                                values.willingToFilmAtLocation
                                                                    ? "Yes, willing to film at my location"
                                                                    : "No"
                                                            }
                                                        />

                                                        {values.willingToFilmAtLocation && (
                                                            <Box sx={{ mt: 2 }}>
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily: "CircularStd",
                                                                        fontSize: "12px",
                                                                        color: T.textSoft,
                                                                        mb: 0.5
                                                                    }}
                                                                >
                                                                    Filming Address
                                                                </Typography>
                                                                <CustomFieldText
                                                                    name="filmingAddress"
                                                                    fullWidth
                                                                    placeholder="Filming Address"
                                                                />
                                                                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                                                    <Grid item xs={5}>
                                                                        <Typography
                                                                            sx={{
                                                                                fontFamily: "CircularStd",
                                                                                fontSize: "12px",
                                                                                color: T.textSoft,
                                                                                mb: 0.5
                                                                            }}
                                                                        >
                                                                            City
                                                                        </Typography>
                                                                        <CustomFieldText
                                                                            name="filmingCity"
                                                                            fullWidth
                                                                            placeholder="City"
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={4}>
                                                                        <Typography
                                                                            sx={{
                                                                                fontFamily: "CircularStd",
                                                                                fontSize: "12px",
                                                                                color: T.textSoft,
                                                                                mb: 0.5
                                                                            }}
                                                                        >
                                                                            State
                                                                        </Typography>
                                                                        <CustomSelectField
                                                                            name="filmingState"
                                                                            label=""
                                                                            noLabel
                                                                            placeholder="State"
                                                                            options={statesList}
                                                                            required={false}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={3}>
                                                                        <Typography
                                                                            sx={{
                                                                                fontFamily: "CircularStd",
                                                                                fontSize: "12px",
                                                                                color: T.textSoft,
                                                                                mb: 0.5
                                                                            }}
                                                                        >
                                                                            ZIP Code
                                                                        </Typography>
                                                                        <CustomFieldText
                                                                            name="filmingZip"
                                                                            fullWidth
                                                                            placeholder="Zip"
                                                                        />
                                                                    </Grid>
                                                                </Grid>

                                                                <Box sx={{ mt: 2.5 }}>
                                                                    <CustomFieldLabel>Location Photos</CustomFieldLabel>
                                                                    <input
                                                                        ref={imageInputRef}
                                                                        type="file"
                                                                        accept="image/jpeg,image/png"
                                                                        multiple
                                                                        style={{ display: "none" }}
                                                                        onChange={async (e) => {
                                                                            const files = e.target.files;
                                                                            if (!files || files.length === 0) return;
                                                                            setUploadingImages(true);
                                                                            try {
                                                                                const uploaded: string[] = [];
                                                                                for (let i = 0; i < files.length; i++) {
                                                                                    const file = files[i];
                                                                                    const ext = file.type.replace(
                                                                                        "image/",
                                                                                        ""
                                                                                    );
                                                                                    const url = await uploadImage(
                                                                                        file,
                                                                                        `location_${Date.now()}_${i}`,
                                                                                        ext
                                                                                    );
                                                                                    if (url) uploaded.push(url);
                                                                                }
                                                                                setLocationImages((prev) => [
                                                                                    ...prev,
                                                                                    ...uploaded
                                                                                ]);
                                                                            } catch {
                                                                                toast.error("Failed to upload images.");
                                                                            } finally {
                                                                                setUploadingImages(false);
                                                                                if (imageInputRef.current)
                                                                                    imageInputRef.current.value = "";
                                                                            }
                                                                        }}
                                                                    />
                                                                    <Box
                                                                        sx={{
                                                                            display: "flex",
                                                                            flexWrap: "wrap",
                                                                            gap: 1.5,
                                                                            mt: 1
                                                                        }}
                                                                    >
                                                                        {locationImages.map((img, idx) => (
                                                                            <Box
                                                                                key={idx}
                                                                                sx={{ position: "relative" }}
                                                                            >
                                                                                <Box
                                                                                    sx={{
                                                                                        width: 100,
                                                                                        height: 100,
                                                                                        minWidth: 100,
                                                                                        overflow: "hidden",
                                                                                        borderRadius: "10px",
                                                                                        backgroundColor: "var(--surface-2)"
                                                                                    }}
                                                                                >
                                                                                    <img
                                                                                        src={img}
                                                                                        alt={`Location ${idx + 1}`}
                                                                                        style={{
                                                                                            width: "100%",
                                                                                            height: "100%",
                                                                                            objectFit: "cover",
                                                                                            borderRadius: "10px"
                                                                                        }}
                                                                                    />
                                                                                </Box>
                                                                                <Box
                                                                                    onClick={() =>
                                                                                        setLocationImages((prev) =>
                                                                                            prev.filter(
                                                                                                (_, i) => i !== idx
                                                                                            )
                                                                                        )
                                                                                    }
                                                                                    sx={{
                                                                                        position: "absolute",
                                                                                        top: 4,
                                                                                        right: 4,
                                                                                        width: 20,
                                                                                        height: 20,
                                                                                        borderRadius: "50%",
                                                                                        backgroundColor:
                                                                                            "rgba(0,0,0,0.6)",
                                                                                        display: "flex",
                                                                                        alignItems: "center",
                                                                                        justifyContent: "center",
                                                                                        cursor: "pointer",
                                                                                        "&:hover": {
                                                                                            backgroundColor:
                                                                                                "rgba(0,0,0,0.8)"
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <CloseIcon
                                                                                        sx={{
                                                                                            fontSize: 12,
                                                                                            color: "#fff"
                                                                                        }}
                                                                                    />
                                                                                </Box>
                                                                            </Box>
                                                                        ))}
                                                                        <Box
                                                                            sx={{
                                                                                width: 100,
                                                                                height: 100,
                                                                                minWidth: 100,
                                                                                cursor: "pointer",
                                                                                overflow: "hidden",
                                                                                borderRadius: "10px",
                                                                                backgroundColor: "var(--surface-2)",
                                                                                display: "flex",
                                                                                justifyContent: "center",
                                                                                alignItems: "center",
                                                                                border: `1.5px dashed ${T.ruleStrong}`
                                                                            }}
                                                                            onClick={() =>
                                                                                imageInputRef.current?.click()
                                                                            }
                                                                        >
                                                                            <img
                                                                                src={imagePlaceholder}
                                                                                alt="placeholder"
                                                                                style={{
                                                                                    width: "100%",
                                                                                    height: "100%",
                                                                                    objectFit: "cover",
                                                                                    borderRadius: "10px",
                                                                                    opacity: 0.5
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    </Box>
                                                                    <Typography
                                                                        sx={{
                                                                            fontFamily: "CircularStd",
                                                                            fontSize: "11px",
                                                                            color: T.textMute,
                                                                            mt: 1
                                                                        }}
                                                                    >
                                                                        Recommended: min 600×600px · .jpg / .png
                                                                    </Typography>
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        sx={{
                                                                            textTransform: "none",
                                                                            borderRadius: "8px",
                                                                            mt: 1,
                                                                            fontSize: "12px",
                                                                            borderColor: T.ruleStrong,
                                                                            color: T.text
                                                                        }}
                                                                        onClick={() => imageInputRef.current?.click()}
                                                                        disabled={uploadingImages}
                                                                    >
                                                                        {uploadingImages
                                                                            ? "Uploading..."
                                                                            : "Upload Image"}
                                                                    </Button>
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    {/* Notes */}
                                                    <Box sx={cardSx}>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "CircularStd",
                                                                fontWeight: 700,
                                                                fontSize: "14px",
                                                                mb: 0.5
                                                            }}
                                                        >
                                                            Notes
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "CircularStd",
                                                                fontSize: "12px",
                                                                color: T.textSoft,
                                                                mb: 1.5
                                                            }}
                                                        >
                                                            Optional additional notes.
                                                        </Typography>
                                                        <CustomTextArea
                                                            name="interestNotes"
                                                            label=""
                                                            placeholder="Add any notes for the brand..."
                                                            rows={3}
                                                        />
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                    </fieldset>
                                )}
                            </Box>

                            {/* ── Action Bar ── */}
                            {!readOnly && (
                                <Box
                                    sx={{
                                        mt: 3,
                                        border: `1px solid ${T.rule}`,
                                        borderRadius: "12px",
                                        boxShadow: "var(--sh-1)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        px: 3,
                                        py: 2,
                                        backgroundColor: T.surface,
                                        gap: 2,
                                        flexWrap: "wrap"
                                    }}
                                >
                                    <Typography sx={{ fontFamily: "CircularStd", fontSize: "13px", color: T.textSoft }}>
                                        {!decisionMade
                                            ? undecidedHint
                                            : values.interested
                                            ? "Accepting confirms your interest and no conflicts with this brand."
                                            : "Your account manager will be notified of your decision."}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                                        <Button
                                            onClick={() => navigate("/expert/projects")}
                                            disabled={isSubmitting}
                                            sx={{
                                                fontFamily: "CircularStd",
                                                textTransform: "none",
                                                borderRadius: "8px",
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                color: T.textSoft,
                                                backgroundColor: "transparent",
                                                "&:hover": { backgroundColor: "var(--surface-2)", color: T.text },
                                                px: 2,
                                                height: 38
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={isSubmitting || videoUploading || uploadingImages}
                                            sx={{
                                                fontFamily: "CircularStd",
                                                textTransform: "none",
                                                borderRadius: "8px",
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                backgroundColor: T.bronze,
                                                "&:hover": { backgroundColor: T.bronzeDeep },
                                                px: 2.5,
                                                height: 38
                                            }}
                                        >
                                            {submitLabel}
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Form>
                );
            }}
        </Formik>
    );
};

export default ConfirmationDetails;
